import type { LegacyVectorSceneV1, PathCommand, PathGeometry, PathNode, VectorObject, VectorScene } from './types';

function finitePoint(x: number, y: number): { x: number; y: number } {
  if (!Number.isFinite(x) || !Number.isFinite(y)) throw new Error('Vector path contains a non-finite coordinate.');
  return { x, y };
}

export function migratePathCommands(commands: readonly PathCommand[], objectId = 'path'): PathGeometry {
  const nodes: PathNode[] = [];
  const subpaths: { id: string; nodeIds: string[]; closed: boolean }[] = [];
  let current: PathNode | undefined;
  let subpath: { id: string; nodeIds: string[]; closed: boolean } | undefined;
  const addNode = (x: number, y: number): PathNode => {
    if (!subpath) {
      const id = `${objectId}:subpath:${subpaths.length}`;
      subpath = { id, nodeIds: [], closed: false };
      subpaths.push(subpath);
    }
    const node: PathNode = {
      id: `${objectId}:node:${nodes.length}`,
      subpathId: subpath.id,
      anchor: finitePoint(x, y),
      incoming: null,
      outgoing: null,
      continuity: 'corner',
    };
    nodes.push(node);
    subpath.nodeIds.push(node.id);
    current = node;
    return node;
  };
  for (const command of commands) {
    if (command.type === 'move') {
      subpath = undefined;
      addNode(command.x, command.y);
    } else if (command.type === 'line') {
      addNode(command.x, command.y);
    } else if (command.type === 'cubic' && current) {
      current.outgoing = finitePoint(command.x1, command.y1);
      const next = addNode(command.x, command.y);
      next.incoming = finitePoint(command.x2, command.y2);
      if (current.outgoing && next.incoming) {
        current.continuity = 'smooth';
        next.continuity = 'smooth';
      }
    } else if (command.type === 'quadratic' && current) {
      const start = current.anchor;
      current.outgoing = finitePoint(start.x + (command.x1 - start.x) * 2 / 3, start.y + (command.y1 - start.y) * 2 / 3);
      const next = addNode(command.x, command.y);
      next.incoming = finitePoint(command.x + (command.x1 - command.x) * 2 / 3, command.y + (command.y1 - command.y) * 2 / 3);
      current.continuity = 'smooth';
      next.continuity = 'smooth';
    } else if (command.type === 'close' && subpath) {
      subpath.closed = true;
    }
  }
  return { nodes, subpaths, fillRule: 'nonzero' };
}

export function migrateVectorScene(value: unknown): VectorScene {
  if (!value || typeof value !== 'object') throw new Error('Vector scene must be an object.');
  const candidate = value as { schemaVersion?: unknown; objects?: unknown; extensions?: unknown };
  if (!Array.isArray(candidate.objects)) throw new Error('Vector scene objects must be an array.');
  if (candidate.schemaVersion === 2) {
    const scene = structuredClone(value as VectorScene);
    scene.objects = scene.objects.map((object) => {
      if (object.type !== 'rectangle' || typeof object.geometry.radius !== 'number') return object;
      const radius = object.geometry.radius as number;
      return { ...object, geometry: { ...object.geometry, radius: { topLeft: radius, topRight: radius, bottomRight: radius, bottomLeft: radius } } } as VectorObject;
    });
    return scene;
  }
  if (candidate.schemaVersion !== 1) throw new Error(`Unsupported vector scene version: ${String(candidate.schemaVersion)}.`);
  const legacy = value as LegacyVectorSceneV1;
  const objects = legacy.objects.map((object): VectorObject => {
    if (object.type !== 'path') {
      const clone = structuredClone(object) as unknown as VectorObject;
      if (clone.type === 'rectangle' && typeof clone.geometry.radius === 'number') {
        const radius = clone.geometry.radius as number;
        clone.geometry.radius = { topLeft: radius, topRight: radius, bottomRight: radius, bottomLeft: radius };
      }
      return clone;
    }
    const geometry = migratePathCommands(object.geometry.commands, object.id);
    geometry.fillRule = object.geometry.fillRule;
    return { ...structuredClone(object), geometry } as VectorObject;
  });
  return { schemaVersion: 2, objects, extensions: structuredClone(legacy.extensions ?? {}) };
}
